import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { closeAllModals, openModal } from 'reducers/modalReducer';
import {
  EmployeeItem,
  EmployeeListForNotificationsResponseBody,
  NotificationSubscriptionStatusResponseBody,
} from 'types/Notifications';
import notificationCenterService from 'services/notificationCenterService';
import { AxiosError } from 'axios';
import { User } from 'types/User';
import { GlobalState } from 'reducers/stateTypes';
import { Form, Formik } from 'formik';
import moment from 'moment';
import { checkIfDateIsValid } from 'components/Common/CustomDateRangePicker';
import {
  ZfBox,
  ZfCheckbox,
  ZfDialog,
  ZfDialogContent,
  ZfDialogFooter,
  ZfDialogHeader,
  ZfFormField,
  ZfInput,
  ZfInputCombined,
  ZfLoadingSpinner,
  ZfStack,
  ZfTextButton,
  ZfToggle,
  ZfTypography,
} from '@zafire/react';
import { DATE_FORMAT } from 'utils/constants';
import ZfSelectInput from 'components/Common/ZfSelectInput';

export type NotificationFormikState = {
  bellChecked: boolean;
  emailChecked: boolean;
  fwdEnabled: boolean;
  fwdTargetUserId: string | null;
  fwdStartDate: Date | null;
  fwdEndDate: Date | null;
};

/**
 * Notification settings modal
 */
const NotificationSettingsModal = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [employeeListLoading, setEmployeeListLoading] = useState<boolean>(false);
  const [employeeList, setEmployeeList] = useState<EmployeeItem[]>([]);
  const [initialValues, setInitialValues] = useState<NotificationFormikState>({
    bellChecked: false,
    emailChecked: false,
    fwdEnabled: false,
    fwdTargetUserId: '',
    fwdStartDate: null,
    fwdEndDate: null,
  });
  const user: User = useSelector((state: GlobalState) => state.user.userDetails);
  const userId = user?.username;
  const dispatch = useDispatch();

  const { getNotificationSubscriptionStatus, getEmployeeListForNotifications, setNotificationSubscriptionStatus } =
    notificationCenterService;

  useEffect(() => {
    setLoading(true);
    getNotificationSubscriptionStatus()
      .then((result: NotificationSubscriptionStatusResponseBody) => {
        if (result?.settings?.findIndex((item) => item.category === 'ALL') >= 0) {
          const settingsForCategoryAll = result.settings.find((item) => item.category === 'ALL');
          const bell = settingsForCategoryAll?.bell ?? false;
          const email = settingsForCategoryAll?.email ?? false;
          const fwdStartDate = moment(settingsForCategoryAll?.forwarding?.startDate, DATE_FORMAT);
          const fwdEndDate = moment(settingsForCategoryAll?.forwarding?.endDate, DATE_FORMAT);
          setInitialValues({
            ...initialValues,
            bellChecked: bell,
            emailChecked: email,
            fwdEnabled: settingsForCategoryAll?.forwarding?.enabled ?? false,
            fwdTargetUserId: settingsForCategoryAll?.forwarding?.targetUserId ?? '',
            fwdStartDate: fwdStartDate.isValid() ? fwdStartDate.toDate() : null,
            fwdEndDate: fwdEndDate.isValid() ? fwdEndDate.toDate() : null,
          });
        } else {
          throw new Error('Received incorrect user settings for notifications.');
        }
      })
      .catch((error: AxiosError | Error) => {
        dispatch(
          openModal({
            modalType: 'JBErrorModalWrapper',
            modalProps: {
              technicalInfo: {
                details: 'Error fetching notification subscription status from pmk task viewer',
                title: 'Error fetching notification subscription status',
                userErrorMessage: 'An error occurred when fetching notification subscription status',
              },
              httpError: error,
            },
          }),
        );
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setEmployeeListLoading(true);
    getEmployeeListForNotifications()
      .then((result: EmployeeListForNotificationsResponseBody) => {
        setEmployeeList(
          result.employees.map((item) => ({
            label: item.name,
            value: item.userId,
          })),
        );
      })
      .catch((error: AxiosError | Error) => {
        dispatch(
          openModal({
            modalType: 'JBErrorModalWrapper',
            modalProps: {
              technicalInfo: {
                details: 'Error fetching employee list for notifications',
                title: 'Error fetching employee list for notifications',
                userErrorMessage: 'An error occurred when fetching employee list for notifications',
              },
              httpError: error,
            },
          }),
        );
      })
      .finally(() => setEmployeeListLoading(false));
  }, []);

  const onClickSaveHandler = (data: NotificationFormikState) => {
    const forwardintEnabled = data.fwdEnabled;
    const statusResponse = {
      userId,
      settings: [
        {
          email: data.emailChecked,
          bell: data.bellChecked,
          category: 'ALL',
          forwarding: {
            enabled: forwardintEnabled,
            targetUserId: forwardintEnabled && data.fwdTargetUserId ? data.fwdTargetUserId : undefined,
            startDate:
              forwardintEnabled && data.fwdStartDate ? moment(data.fwdStartDate).format(DATE_FORMAT) : undefined,
            endDate: forwardintEnabled && data.fwdEndDate ? moment(data.fwdEndDate).format(DATE_FORMAT) : undefined,
          },
        },
      ],
    };
    setNotificationSubscriptionStatus(statusResponse)
      .then((_: NotificationSubscriptionStatusResponseBody) => {
        dispatch(closeAllModals());
      })
      .catch((error: AxiosError | Error) => {
        dispatch(
          openModal({
            modalType: 'JBErrorModalWrapper',
            modalProps: {
              technicalInfo: {
                details: 'Error updating notification subscription status',
                title: 'Error updating notification subscription status',
                userErrorMessage: 'An error occurred when updating notification subscription status',
              },
              httpError: error,
            },
          }),
        );
      });
  };
  const validateFormHandler = (values: NotificationFormikState) => {
    const errors: {
      bellChecked?: string;
      emailChecked?: string;
      fwdEnabled?: string;
      fwdTargetUserId?: string;
      fwdStartDate?: string;
      fwdEndDate?: string;
    } = {};
    if (!values.bellChecked && !values.emailChecked) {
      errors.bellChecked = 'Notification option is required';
    }
    if (values.fwdEnabled) {
      if (!values.fwdTargetUserId) {
        errors.fwdTargetUserId = 'Enter user name';
      }

      if (
        !values.fwdStartDate ||
        !checkIfDateIsValid(values.fwdStartDate) ||
        !values.fwdEndDate ||
        !checkIfDateIsValid(values.fwdEndDate) ||
        moment(values.fwdEndDate).isBefore(values.fwdStartDate, 'days')
      ) {
        errors.fwdStartDate = 'Enter a valid date range';
        errors.fwdEndDate = 'Enter a valid date range';
      } else if (checkIfDateIsValid(values.fwdStartDate) && checkIfDateIsValid(values.fwdEndDate)) {
        if (
          moment(values.fwdStartDate).isBefore(new Date(), 'days') ||
          moment(values.fwdEndDate).isBefore(new Date(), 'days')
        ) {
          errors.fwdStartDate = 'Date range should be in the future';
          errors.fwdEndDate = 'Date range should be in the future';
        }
      }
    }
    return errors;
  };

  return (
    <ZfDialog size="small" show={true} onDialogClose={() => dispatch(closeAllModals())}>
      <Formik
        initialValues={initialValues}
        onSubmit={(data) => onClickSaveHandler(data)}
        enableReinitialize
        validate={validateFormHandler}
        validateOnBlur={false}
        validateOnChange={true}
      >
        {({ errors, values, dirty, setFieldValue, getFieldProps, submitForm }) => {
          return (
            <Form>
              <ZfDialogHeader>{t('notification.settingsModal.title')}</ZfDialogHeader>
              <ZfDialogContent>
                {loading && (
                  <ZfStack alignment="center">
                    <ZfLoadingSpinner aria-label="loading" size="large" color="primary" type="infinite" />
                  </ZfStack>
                )}

                <ZfStack direction="column" spacing="extra-large">
                  <ZfBox>
                    <ZfStack direction="row" style={{ paddingBottom: 'var(--gap-vertical-md)' }}>
                      <ZfTypography variant="label-medium" weight="bold">
                        {t('notification.settingsModal.channelLabel')}
                      </ZfTypography>
                    </ZfStack>
                    <ZfStack direction="row" alignment="flex-start">
                      <ZfFormField
                        alerts={[errors.bellChecked ?? '']}
                        error={Boolean(errors.bellChecked || errors.emailChecked)}
                      >
                        <ZfStack direction="row" spacing="medium">
                          <ZfCheckbox
                            {...getFieldProps('bellChecked')}
                            label={t('notification.settingsModal.channelClmLabel')}
                            name="bellChecked"
                            color="primary"
                            checked={values.bellChecked}
                            onCheckboxCheck={getFieldProps('bellChecked').onChange}
                            disabled={loading}
                          />

                          <ZfCheckbox
                            {...getFieldProps('emailChecked')}
                            label={t('notification.settingsModal.channelEmailLabel')}
                            name="emailChecked"
                            color="primary"
                            checked={values.emailChecked}
                            onCheckboxCheck={getFieldProps('emailChecked').onChange}
                            disabled={loading}
                            data-testid="email-checkbox"
                          />
                        </ZfStack>
                      </ZfFormField>
                    </ZfStack>
                  </ZfBox>
                  <ZfBox>
                    <ZfStack direction="row" style={{ paddingBottom: 'var(--gap-vertical-md)' }}>
                      <ZfTypography variant="label-medium" weight="bold">
                        {t('notification.settingsModal.forwardLabel')}
                      </ZfTypography>
                    </ZfStack>
                    <ZfStack direction="row" alignment="flex-start">
                      <ZfFormField alerts={[errors.fwdEnabled ?? '']}>
                        <ZfToggle
                          {...getFieldProps('fwdEnabled')}
                          label={t('notification.settingsModal.forwardLabel')}
                          name="fwdEnabled"
                          id="fwd-notification-toggle"
                          data-testid="fwd-notification-toggle"
                          checked={values.fwdEnabled}
                          onToggleChange={getFieldProps('fwdEnabled').onChange}
                          disabled={loading}
                        />
                      </ZfFormField>
                    </ZfStack>
                  </ZfBox>
                  {values.fwdEnabled && (
                    <>
                      <ZfBox>
                        <ZfSelectInput
                          placeholder={t('notification.settingsModal.userDropdownPlaceholderLabel')}
                          label={t('notification.settingsModal.userDropdownLabel')}
                          name="fwdTargetUserId"
                          options={employeeList}
                          loading={employeeListLoading}
                          searchInLabelAndValue
                        />
                      </ZfBox>
                      <ZfBox>
                        <ZfFormField
                          label={t('notification.settingsModal.dateRangeLabel')}
                          alerts={[errors.fwdStartDate ?? '']}
                        >
                          <ZfInputCombined>
                            <ZfInput
                              {...getFieldProps('fwdStartDate')}
                              error={Boolean(errors.fwdStartDate)}
                              name="fwdStartDate"
                              type="date"
                              placeholder="DD/MM/YYYY"
                              dateCalendarProps={{
                                min: new Date().toISOString(),
                                composite: true,
                              }}
                              value={values.fwdStartDate ? new Date(values.fwdStartDate).toISOString() : ''}
                              onInputChange={(ev) => {
                                const value = ev.detail;
                                if (!value) {
                                  setFieldValue('fwdStartDate', '');
                                  return;
                                }
                                const startDate = new Date(ev.detail);
                                setFieldValue('fwdStartDate', startDate);
                                // Reset end date if it becomes invalid
                                if (values.fwdEndDate && new Date(values.fwdEndDate) < new Date(startDate)) {
                                  setFieldValue('fwdEndDate', '');
                                }
                              }}
                            />

                            <ZfInput
                              {...getFieldProps('fwdEndDate')}
                              name="fwdEndDate"
                              type="date"
                              placeholder="DD/MM/YYYY"
                              error={Boolean(errors.fwdEndDate)}
                              dateCalendarProps={{
                                min: values.fwdStartDate ? new Date(values.fwdStartDate).toISOString() : undefined,
                                composite: true,
                              }}
                              value={values.fwdEndDate ? new Date(values.fwdEndDate).toISOString() : ''}
                              onInputChange={(ev) => {
                                const value = ev.detail;
                                if (!value) {
                                  setFieldValue('fwdEndDate', '');
                                  return;
                                }
                                const endDate = new Date(ev.detail);
                                setFieldValue('fwdEndDate', endDate);
                              }}
                            />
                          </ZfInputCombined>
                        </ZfFormField>
                      </ZfBox>
                    </>
                  )}
                </ZfStack>
              </ZfDialogContent>
              <ZfDialogFooter>
                <ZfStack slot="primary" direction="row" spacing="small">
                  <ZfTextButton size="medium" hierarchy="tertiary" onClick={() => dispatch(closeAllModals())}>
                    {t('notification.settingsModal.cancelButtonLabel')}
                  </ZfTextButton>
                  <ZfTextButton
                    onClick={submitForm}
                    type="submit"
                    hierarchy="primary"
                    size="medium"
                    disabled={!dirty || loading}
                  >
                    {t('notification.settingsModal.saveButtonLabel')}
                  </ZfTextButton>
                </ZfStack>
              </ZfDialogFooter>
            </Form>
          );
        }}
      </Formik>
    </ZfDialog>
  );
};

export default NotificationSettingsModal;
